import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

interface NavSection {
    id: string;
    label: string;
    icon: string;
}

interface NavGroup {
    label: string;
    items: NavSection[];
}

interface GetEndpoint {
    path: string;
    description: string;
    example: string;
}

@Component({
    selector: 'convfact-developers',
    templateUrl: './developers.component.html',
    styleUrls: ['./developers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class DevelopersComponent implements AfterViewInit, OnDestroy {
    navGroups: NavGroup[] = [
        {
            label: 'Empezar',
            items: [
                { id: 'intro', label: 'Introducción', icon: 'description' },
                { id: 'auth', label: 'Autenticación', icon: 'vpn_key' },
                { id: 'dates', label: 'Formatos de fecha', icon: 'event' },
            ],
        },
        {
            label: 'Recursos',
            items: [
                { id: 'queries', label: 'Consultas (GET)', icon: 'search' },
                { id: 'invoices', label: 'Crear facturas', icon: 'receipt' },
                { id: 'bankaccounts', label: 'Crear cuentas bancarias', icon: 'account_balance' },
            ],
        },
        {
            label: 'Referencia',
            items: [
                { id: 'errors', label: 'Códigos de error', icon: 'warning' },
                { id: 'changelog', label: 'Historial de cambios', icon: 'history' },
            ],
        },
    ];

    sections: NavSection[] = this.navGroups.flatMap(g => g.items);

    activeSectionId = this.sections[0].id;
    sidebarOpen = false;
    scrollProgress = 0;

    apiKeyResponse = `{
  "apiKey": "3f1c9e2b4a7d4c9e8b1a2c3d4e5f6789"
}`;

    authCurl = `curl -X POST https://api.handly.es/auth/token \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "username=tu@email.com" \\
  -d "password=TU_API_KEY" \\
  -d "grant_type=password"`;

    authResponse = `{
  "access_token": "eyJhbGciOiJBMjU2S1ci...",
  "token_type": "Bearer",
  "expires_in": 604799
}`;

    getEndpoints: GetEndpoint[] = [
        {
            path: '/public/v1/activities',
            description: 'Consulta las actividades económicas del usuario.',
            example: `[{ "uid": "e11a5519-...", "name": "Principal" }]`,
        },
        {
            path: '/public/v1/contacts',
            description: 'Consulta los contactos (clientes/proveedores) del usuario.',
            example: `[{ "uid": "87c58ebd-...", "companyName": "Dislecan" }]`,
        },
        {
            path: '/public/v1/taxes',
            description: 'Consulta los impuestos configurados en el sistema (IVA, IGIC, IRPF...).',
            example: `[{ "uid": "1cc35fe1-...", "name": "IVA general (21%)" }]`,
        },
        {
            path: '/public/v1/bankaccounts',
            description: 'Consulta las cuentas bancarias registradas del usuario.',
            example: `[{ "uid": "e6ada056-...", "iban": "ES3501825310610201606882" }]`,
        },
        {
            path: '/public/v1/efectivo',
            description: 'Consulta las cajas de efectivo del usuario.',
            example: `[{ "id": 1, "nombre": "Caja principal", "ubicacion": "Oficina" }]`,
        },
    ];

    invoiceRequestExample = `{
  "contactId": "87c58ebd-0fe1-4b70-8950-d78256bd574b",
  "activityId": "e11a5519-4e46-483c-a0ab-6cbdf306d0ad",
  "date": "21-07-2026",
  "dueDate": "21-08-2026",
  "fiscalYear": 2026,
  "currency": "EUR",
  "notes": "Servicio de consultoría",
  "paymentMethod": {
    "id": "e6ada056-9c02-4c7f-a5a9-00b056a9ef41",
    "type": "Bank",
    "datePaided": "21-07-2026"
  },
  "items": [
    {
      "name": "Consultoría",
      "units": 1,
      "amount": 100,
      "discount": 0,
      "taxes": ["1cc35fe1-b983-40b2-844f-b54d4cee2777"]
    }
  ],
  "invoiceTypeCode": "F1"
}`;

    invoiceResponseExample = `{
  "success": true,
  "invoiceId": "9aa6c475-7390-4ba8-a495-f22d13e87f7d",
  "status": "created",
  "contactId": "87c58ebd-0fe1-4b70-8950-d78256bd574b",
  "message": null,
  "errorCode": null,
  "errors": null
}`;

    rectifyRequestExample = `{
  "activityId": "e11a5519-4e46-483c-a0ab-6cbdf306d0ad",
  "invoiceTypeCode": "R1",
  "rectifiedInvoices": [
    { "invoiceNumber": "FRA-00015" },
    { "invoiceNumber": "FRA-00016" }
  ]
}`;

    bankAccountRequestExample = `{
  "iban": "ES8301822384267012345678",
  "bank": "Banco de Pruebas",
  "swift": "BSCHESMMXXX",
  "remesa": false,
  "taxationDefault": false
}`;

    bankAccountResponseExample = `{
  "bankAccountId": "b77c68ee-b6b9-4a40-9299-02d15b6dfa68",
  "success": true,
  "message": null,
  "errorCode": null,
  "errors": null
}`;

    errorResponseExample = `{
  "success": false,
  "message": "Validation errors",
  "errorCode": "INVALID_REQUEST",
  "errors": ["Iban: Iban is not a valid IBAN"]
}`;

    @ViewChildren('sectionEl') sectionEls: QueryList<ElementRef>;

    private observer: IntersectionObserver;

    constructor(private router: Router) { }

    ngAfterViewInit(): void {
        this.observer = new IntersectionObserver(
            entries => {
                const visible = entries.filter(e => e.isIntersecting);
                if (visible.length > 0) {
                    this.activeSectionId = visible[0].target.id;
                }
            },
            { rootMargin: '-10% 0px -80% 0px' }
        );

        this.sectionEls.forEach(el => this.observer.observe(el.nativeElement));
    }

    ngOnDestroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    // El host es el propio contenedor con scroll (ver nota en developers.component.scss),
    // así que el listener escucha directamente sobre su elemento nativo.
    @HostListener('scroll', ['$event'])
    onScroll(event: Event): void {
        const el = event.target as HTMLElement;
        const max = el.scrollHeight - el.clientHeight;
        this.scrollProgress = max > 0 ? Math.min(100, (el.scrollTop / max) * 100) : 0;
    }

    scrollTo(id: string): void {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
        this.sidebarOpen = false;
    }

    toggleSidebar(): void {
        this.sidebarOpen = !this.sidebarOpen;
    }

    goToLogin(): void {
        this.router.navigate(['/login']);
    }
}
